import moment from "moment";

// Shared "which package counts as a client's current one, and what's its status" logic —
// extracted from Members/index.js's `memberRows` useMemo (same behavior, moved so
// NewAppointmentFlow.jsx can show the same status without re-deriving it a second way).
//
// Selection: prefer the soonest-to-expire package that's still active (not past its end date
// and not fully used) — not simply the most recently started one, so a client with several
// packages (e.g. an old expired one plus a fresh renewal) always surfaces the one that's
// actually usable right now. Falls back to the last package by end date if none qualify (so an
// expired/used-up client still shows *something* instead of nothing).
//
// `packages` is one client's own package rows (i.e. `packagesByClient[clientId] || []`, the
// exact shape `client-packages/batch` already returns and membersStore.js already caches).
export function selectCurrentPackage(packages) {
  const list = packages || [];

  // `end_date` is the authoritative, server-computed value (added alongside the
  // package-assignment flow). Older rows created before that existed fall back to the
  // previous day-count estimate so they still render something reasonable.
  const withEffectiveEnd = list.map((pkg) => ({
    pkg,
    effectiveEnd: pkg.end_date
      ? moment(pkg.end_date)
      : moment(pkg.start_date).add(pkg.package_period_days || 0, "days"),
  }));
  const byEndDateAsc = [...withEffectiveEnd].sort((a, b) =>
    a.effectiveEnd.diff(b.effectiveEnd)
  );

  const today = moment();
  // 1. The first (soonest to expire) package that's still active and not fully used.
  const activeEntry = byEndDateAsc.find(
    ({ pkg, effectiveEnd }) =>
      !effectiveEnd.isBefore(today, "day") &&
      (pkg.package_service_qty ?? 0) > (pkg.total_used ?? pkg.used ?? 0)
  );
  // 2. Otherwise, the last package sorted by end date.
  const fallbackEntry = byEndDateAsc[byEndDateAsc.length - 1];
  const selectedEntry = activeEntry || fallbackEntry;

  const currentPackage = selectedEntry?.pkg;
  let status = "none";
  let validUntil = null;
  let usedSessions = 0;
  let totalSessions = 0;
  if (currentPackage) {
    validUntil = selectedEntry.effectiveEnd;
    totalSessions =
      currentPackage.total_sessions ?? currentPackage.package_service_qty ?? 0;
    usedSessions = Math.min(
      currentPackage.total_used ?? currentPackage.used ?? 0,
      totalSessions || Number.MAX_SAFE_INTEGER
    );
    // No `totalSessions > 0` guard: a 0-qty package (e.g. the legacy-backfill placeholder) has
    // 0 used >= 0 total immediately, so it reads as used-up (inactive) rather than perpetually
    // "active".
    const isUsedUp = usedSessions >= totalSessions;
    const isPastEnd = validUntil.isBefore(moment(), "day");
    status = isUsedUp || isPastEnd ? "inactive" : "active";
  }

  return { currentPackage, status, validUntil, usedSessions, totalSessions };
}
